import { Box, Center, Flex, Text, Input, Button, Icon, IconButton } from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import { PlusIcon, Trash04Icon } from '../../../assets/icons'

interface CustomSwitchProps {
  initialState: number
  showToggle?: boolean
  onToggle: (state: number) => void
  onExamplesChange: (examples: BehaviorExamples) => void
  type: 'skill' | 'competence' | 'task'
}
export interface BehaviorExamples {
  foundational: string[]
  operational: string[]
  tactical: string[]
  strategic: string[]
}

const labelMap = ['foundational', 'operational', 'tactical', 'strategic']
const initialExamples: BehaviorExamples = {
  foundational: [],
  operational: [],
  tactical: [],
  strategic: [],
}

const CustomSwitch: React.FC<CustomSwitchProps> = ({ initialState, onToggle, onExamplesChange, type }) => {
  const [currentState, setCurrentState] = useState(initialState)
  const [examples, setExamples] = useState<BehaviorExamples>(initialExamples)

  const positions = ['1%', '25%', '50%', '74%']
  const labels = type == 'competence' ? ['Foundational', 'Operational', 'Tactical', 'Strategic'] : type == 'task' ? ['Assisted', 'Independent', 'Complex', 'Leadership'] : ['Basic', 'Intermediate', 'Advanced', 'Specialist']

  const currentLabelKey = labelMap[currentState] as keyof BehaviorExamples

  // When switching from one label to another, call onToggle
  const handleToggle = (index: number) => {
    setCurrentState(index)
    onToggle(index)
  }

  // Add an empty string to the examples array for the current label.
  const handleAddField = () => {
    setExamples((prev) => ({
      ...prev,
      [currentLabelKey]: [...prev[currentLabelKey], ''],
    }))
  }

  const handleInputChange = (fieldIndex: number, value: string) => {
    setExamples((prev) => {
      const updatedArray = [...prev[currentLabelKey]]
      updatedArray[fieldIndex] = value
      return {
        ...prev,
        [currentLabelKey]: updatedArray,
      }
    })
  }

  const handleRemoveField = (fieldIndex: number) => {
    setExamples((prev) => {
      const updatedArray = prev[currentLabelKey].filter((_, index) => index !== fieldIndex)
      return {
        ...prev,
        [currentLabelKey]: updatedArray,
      }
    })
  }

  useEffect(() => {
    onExamplesChange(examples)
  }, [examples, onExamplesChange])

  return (
    <Flex direction="column" gap={4}>
      <Flex
        w="100%"
        h="44px"
        borderRadius="8px"
        pos="relative"
        bg="solid.25"
        justifyContent="space-between"
        alignItems="center"
        transition="0.2s ease-out"
        p={2}
      >
        <Box
          transition="0.2s ease-out"
          pos="absolute"
          w="25%"
          h="36px"
          bg="solid.0"
          borderRadius="6px"
          left={positions[currentState]}
          boxShadow="0px 0px 3px rgba(0, 0, 0, 0.08), 0px 3px 1px rgba(0, 0, 0, 0.05)"
        />
        {labels.map((label, index) => (
          <Center
            key={index}
            w="25%"
            pos="absolute"
            left={positions[index]}
            cursor="pointer"
            onClick={() => {
              handleToggle(index)
            }}
          >
            <Text fontSize="14px">{label}</Text>
          </Center>
        ))}
      </Flex>

      <Flex direction="column" gap={3}>
        {examples[currentLabelKey]?.map((field, index) => (
          <Flex key={index} alignItems="center" gap={2}>
            <Input
              placeholder={`Example ${index + 1}`}
              value={field}
              onChange={(e) => handleInputChange(index, e.target.value)}
            />
            <IconButton
              onClick={() => handleRemoveField(index)}
              aria-label="Remove"
              variant="error"
              icon={
                <Icon w="24px" h="24px" viewBox="0 0 24 24" fill="none">
                  <Trash04Icon />
                </Icon>
              }
            />
          </Flex>
        ))}
        <Button variant="examples" onClick={handleAddField}>
          <Icon w="20px" h="20px" viewBox="0 0 24 24" fill="primary.100" color="primary.100" mr={2}>
            <PlusIcon />
          </Icon>
          Add New Example
        </Button>
      </Flex>
    </Flex>
  )
}

export default CustomSwitch
